import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Typography, Button, Dialog, DialogContent, DialogActions, useTheme } from '@mui/material';
import {
  Fullscreen as FullscreenIcon,
  FormatAlignLeft as FormatIcon,
  CheckCircle as ValidIcon,
  ErrorOutline as InvalidIcon,
} from '@mui/icons-material';
import CodeMirror from '@uiw/react-codemirror';
import { json, jsonParseLinter } from '@codemirror/lang-json';
import { linter, lintGutter } from '@codemirror/lint';
import { EditorView } from '@codemirror/view';

interface JsonCodeEditorProps {
  label?: string;
  value: Record<string, unknown>;
  onChange: (parsed: Record<string, unknown>) => void;
  onValidityChange?: (valid: boolean, error?: string) => void;
  required?: boolean;
  externalError?: string;
  placeholder?: Record<string, unknown>;
  minHeight?: number;
  /** Read-only view: disables editing and the Format action; keeps folding. */
  readOnly?: boolean;
  /** Extra controls rendered in the status line (e.g. a copy-to-clipboard button). */
  actions?: React.ReactNode;
  /** Show the Expand-to-fullscreen control (default true). */
  showExpand?: boolean;
}

/** Turn a JSON.parse error into a human line/column message. Modern engines
 * append "(line N column M)"; fall back to the raw message otherwise. */
function formatParseError(message: string): string {
  const lc = message.match(/line (\d+) column (\d+)/i);
  if (lc) {
    const head = message.replace(/\s*in JSON.*$/i, '').replace(/\s*$/, '');
    return `Line ${lc[1]}, column ${lc[2]}: ${head}`;
  }
  return message.replace(/\s*at position \d+\s*/i, ' ');
}

const JsonCodeEditor: React.FC<JsonCodeEditorProps> = ({
  label,
  value,
  onChange,
  onValidityChange,
  required,
  externalError,
  placeholder,
  minHeight = 160,
  readOnly = false,
  actions,
  showExpand = true,
}) => {
  const theme = useTheme();
  const hasValue = (v?: Record<string, unknown>) => !!v && Object.keys(v).length > 0;
  const fallback = useMemo(
    () => (placeholder ? JSON.stringify(placeholder, null, 2) : '{}'),
    [placeholder],
  );
  const seed = useMemo(
    () => (hasValue(value) ? JSON.stringify(value, null, 2) : fallback),
    // seed is computed once on mount; external changes go through the effect below
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [text, setText] = useState(seed);
  const [parseError, setParseError] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [formatNote, setFormatNote] = useState<string | null>(null);
  const lastEmitted = useRef(JSON.stringify(value));

  // Re-seed only when the value changes from an external source (a different
  // rule/template loaded), never from our own emitted edits — that would
  // reformat mid-type and move the cursor.
  useEffect(() => {
    const incoming = JSON.stringify(value);
    if (incoming !== lastEmitted.current) {
      lastEmitted.current = incoming;
      setText(hasValue(value) ? JSON.stringify(value, null, 2) : fallback);
      setParseError(null);
      onValidityChange?.(true);
    }
    // hasValue/fallback are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, onValidityChange]);

  const handleChange = useCallback(
    (next: string) => {
      setText(next);
      setFormatNote(null);
      if (!next.trim()) {
        setParseError(null);
        lastEmitted.current = JSON.stringify({});
        onChange({});
        onValidityChange?.(true);
        return;
      }
      try {
        const parsed = JSON.parse(next);
        setParseError(null);
        lastEmitted.current = JSON.stringify(parsed);
        onChange(parsed);
        onValidityChange?.(true);
      } catch (err) {
        const msg = formatParseError(err instanceof Error ? err.message : 'Invalid JSON');
        setParseError(msg);
        onValidityChange?.(false, msg);
      }
    },
    [onChange, onValidityChange],
  );

  const handleFormat = useCallback(() => {
    try {
      const parsed = JSON.parse(text);
      const pretty = JSON.stringify(parsed, null, 2);
      setText(pretty);
      lastEmitted.current = JSON.stringify(parsed);
      onChange(parsed);
      setParseError(null);
      onValidityChange?.(true);
      setFormatNote(null);
    } catch {
      setFormatNote("Can't format — fix the highlighted error first.");
    }
  }, [text, onChange, onValidityChange]);

  const cmTheme = useMemo(
    () =>
      EditorView.theme(
        {
          '&': {
            backgroundColor: 'transparent',
            fontSize: '0.8rem',
            color: theme.palette.text.primary,
          },
          '.cm-content': {
            fontFamily: '"JetBrains Mono", "Roboto Mono", monospace',
            caretColor: theme.palette.primary.main,
          },
          '.cm-gutters': {
            backgroundColor: 'transparent',
            color: theme.palette.text.disabled,
            border: 'none',
          },
          '.cm-activeLine': { backgroundColor: theme.palette.action.hover },
          '.cm-activeLineGutter': { backgroundColor: theme.palette.action.hover },
          '&.cm-focused': { outline: 'none' },
          '.cm-cursor': { borderLeftColor: theme.palette.primary.main },
        },
        { dark: theme.palette.mode === 'dark' },
      ),
    [theme],
  );

  const extensions = useMemo(
    () => [
      json(),
      linter(jsonParseLinter()),
      lintGutter(),
      cmTheme,
      EditorView.lineWrapping,
      ...(readOnly ? [EditorView.editable.of(false)] : []),
    ],
    [cmTheme, readOnly],
  );

  const isEmpty = !text.trim();

  const borderColor = externalError || parseError
    ? theme.palette.error.main
    : theme.palette.divider;

  const editor = (height: number) => (
    <Box
      sx={{
        border: 1,
        borderColor,
        borderRadius: 2,
        overflow: 'hidden',
        '&:focus-within': {
          borderColor: externalError || parseError ? 'error.main' : 'primary.main',
        },
        '& .cm-editor': { minHeight: height },
      }}
    >
      <CodeMirror
        value={text}
        onChange={handleChange}
        theme={theme.palette.mode === 'dark' ? 'dark' : 'light'}
        extensions={extensions}
        basicSetup={{
          lineNumbers: true,
          foldGutter: true,
          highlightActiveLine: true,
          bracketMatching: true,
          closeBrackets: true,
          autocompletion: false,
          highlightActiveLineGutter: true,
        }}
      />
    </Box>
  );

  const statusLine = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, minHeight: 22 }}>
      {readOnly ? (
        <Box sx={{ flex: 1 }} />
      ) : externalError || parseError ? (
        <>
          <InvalidIcon sx={{ fontSize: 15, color: 'error.main' }} />
          <Typography variant="caption" color="error.main" sx={{ flex: 1 }}>
            {externalError || parseError}
            {parseError && !externalError && ' — changes not applied until fixed'}
          </Typography>
        </>
      ) : isEmpty ? (
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {required ? 'Empty — required' : 'Empty'}
        </Typography>
      ) : (
        <>
          <ValidIcon sx={{ fontSize: 15, color: 'success.main' }} />
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            Valid JSON
          </Typography>
        </>
      )}
      {actions}
      {!readOnly && (
        <Button size="small" startIcon={<FormatIcon />} onClick={handleFormat}>Format</Button>
      )}
      {showExpand && (
        <Button size="small" startIcon={<FullscreenIcon />} onClick={() => setFullscreen(true)}>Expand</Button>
      )}
    </Box>
  );

  return (
    <Box>
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
          {label}{required && <Box component="span" sx={{ color: 'error.main' }}> *</Box>}
        </Typography>
      )}
      {editor(minHeight)}
      {formatNote && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
          {formatNote}
        </Typography>
      )}
      {statusLine}

      <Dialog open={fullscreen} onClose={() => setFullscreen(false)} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 2 }}>
          {label && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
              {label}
            </Typography>
          )}
          {editor(440)}
          {statusLine}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button size="small" variant="contained" onClick={() => setFullscreen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JsonCodeEditor;
